/** WebSocket protocol shared by the server, web and mobile clients. */

export type ClientMsg =
  | { t: "hello"; token: string }
  | { t: "room.join"; roomId: string }
  | { t: "room.leave"; roomId: string }
  | { t: "room.ready"; roomId: string; ready: boolean }
  | { t: "room.start"; roomId: string }
  | { t: "game.move"; roomId: string; move: unknown; moveNo: number }
  | { t: "chat.send"; roomId: string; text?: string; phrase?: string }
  | { t: "dm.typing"; to: string; typing: boolean }
  | { t: "call.invite"; to: string; callId: string }
  | { t: "call.accept"; callId: string }
  | { t: "call.decline"; callId: string }
  | { t: "call.end"; callId: string }
  | { t: "call.signal"; callId: string; data: CallSignal }
  | { t: "call.mute"; callId: string; muted: boolean }
  | { t: "voice.join"; roomId: string; sid: string }
  | { t: "voice.leave"; roomId: string }
  | { t: "voice.mute"; roomId: string; muted: boolean }
  | { t: "voice.signal"; roomId: string; to: string; data: CallSignal }
  | { t: "ping" };

/** WebRTC signalling payload relayed (never inspected beyond its shape) between the two call parties. */
export type CallSignal =
  | { kind: "offer" | "answer"; sdp: string }
  | { kind: "candidate"; candidate: { candidate: string; sdpMid?: string | null; sdpMLineIndex?: number | null } | null };

export interface CallPeer {
  id: string;
  username: string;
  displayName: string;
  avatarUrl: string | null;
}

export type CallEndReason = "ended" | "declined" | "cancelled" | "missed" | "busy" | "connection_lost" | "failed";

export type ServerMsg =
  | { t: "welcome"; userId: string }
  | { t: "error"; code: string; ref?: string }
  | { t: "room.state"; room: RoomSnapshot }
  | { t: "game.state"; roomId: string; matchId: string; seat: number; view: unknown; moveNo: number; deadline: number | null }
  | { t: "game.events"; roomId: string; events: unknown[]; moveNo: number }
  | { t: "game.over"; roomId: string; result: unknown }
  | { t: "chat.msg"; roomId: string; msg: ChatMessage }
  | { t: "gift.received"; gift: unknown }
  | { t: "notify"; notification: unknown }
  | { t: "presence"; userId: string; online: boolean }
  | { t: "dm.msg"; msg: DirectMessage }
  | { t: "dm.typing"; from: string; typing: boolean }
  | { t: "call.incoming"; callId: string; from: CallPeer }
  | { t: "call.state"; callId: string; state: "ringing" | "active"; role: "caller" | "callee"; peer: CallPeer; peerMuted: boolean }
  | { t: "call.signal"; callId: string; data: CallSignal }
  | { t: "call.mute"; callId: string; muted: boolean }
  | { t: "call.ended"; callId: string; reason: CallEndReason }
  | { t: "voice.roster"; roomId: string; max: number; participants: VoiceParticipant[] }
  | { t: "voice.signal"; roomId: string; from: string; sid: string; data: CallSignal }
  | { t: "room.removed"; roomId: string; reason: "kicked" | "closed" }
  | { t: "pong" };

/** A player in a room's group voice chat. Audio itself travels peer-to-peer; the server only keeps this list. */
export interface VoiceParticipant {
  userId: string;
  username: string;
  avatar: string | null;
  /** Per-join session id chosen by the client, so a rejoin replaces the old connection. */
  sid: string;
  /** The player switched their own microphone off. */
  muted: boolean;
  /** A moderator muted this player in the room. The server refuses to let them send audio. */
  serverMuted: boolean;
}

/** Group voice limit: a full mesh of WebRTC connections. Larger rooms need an SFU (see docs/voice.md). */
export const VOICE_MAX_PARTICIPANTS = 4;

export interface DirectMessage {
  id: string;
  senderId: string;
  recipientId: string;
  text: string;
  at: string;
  readAt: string | null;
}

export interface RoomSnapshot {
  id: string;
  code: string;
  gameId: string;
  visibility: "public" | "private";
  maxPlayers: number;
  /** The room moderator. `hostId` is kept for existing clients and has the same value. */
  hostId: string;
  moderatorId: string;
  status: "waiting" | "playing" | "finished";
  seats: { userId: string; username: string; avatar: string | null; ready: boolean; connected: boolean; voiceMuted: boolean }[];
  matchId: string | null;
}

export interface ChatMessage {
  id: string;
  userId: string;
  username: string;
  text: string;
  at: string;
}

/** Quick phrases (ids are sent instead of free text). */
export const QUICK_PHRASES: Record<string, { ar: string; en: string }> = {
  hi: { ar: "هلا والله", en: "Hey there" },
  gg: { ar: "لعب حلو", en: "Good game" },
  wow: { ar: "يا سلام!", en: "Wow!" },
  hurry: { ar: "يلا بسرعة", en: "Hurry up" },
  thanks: { ar: "مشكور", en: "Thanks" },
  oops: { ar: "أفا!", en: "Oops!" },
  again: { ar: "نعيدها؟", en: "Rematch?" },
  luck: { ar: "بالتوفيق", en: "Good luck" },
};
